import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type {
  SlotScreenId,
  ScreenConfig,
  ScreenResult,
  ScreenGenerationSettings,
  ScreenSavedToast,
  IdeaChatMessage,
  IdeaChatSession,
  IdeaChatModelId,
  IdeaCanvasState,
  VideoCompilationConfig,
  VideoCompilationState,
  VideoSlotImage,
  VideoGenerationHistoryEntry,
  VideoResolutionPreset,
  SlotScreenGeneratorState,
  SystemPromptEditorPrefs,
} from '../../core/types/slot-screen-generator.types';
import { SLOT_SCREEN_ORDER } from '../../core/types/slot-screen-generator.types';
import type { ScreenComponentDef, ComponentResult } from '../../core/types/slot-screen-component.types';
import { buildInitialScreenComponents, DEFAULT_SCREEN_COMPONENTS } from '../constants/slot-screen-component-defaults';
import {
  DEFAULT_SYSTEM_PROMPT_JSON,
  DEFAULT_SCREEN_CONFIGS,
  DEFAULT_GENERATION_SETTINGS,
  DEFAULT_VIDEO_CONFIG,
  VIDEO_RESOLUTION_PRESETS,
  DEFAULT_ANIMATION_SYSTEM_PROMPTS,
  buildInitialVideoSlotImages,
} from '../constants/slot-screen-defaults';

const generateId = () => Math.random().toString(36).substring(2, 11);

const DEFAULT_EDITOR_PREFS: SystemPromptEditorPrefs = {
  fontSize: 12,
  tabSize: 2,
  lineNumbers: true,
  minimap: true,
  wordWrap: true,
};

function buildInitialScreens(): Record<SlotScreenId, ScreenConfig> {
  const screens = {} as Record<SlotScreenId, ScreenConfig>;
  for (const id of SLOT_SCREEN_ORDER) {
    const base = DEFAULT_SCREEN_CONFIGS[id];
    screens[id] = {
      ...base,
      generatedResults: [],
      selectedResultId: null,
      status: 'empty',
    };
  }
  return screens;
}

// ─── Actions ───────────────────────────────────────────────
interface SlotScreenGeneratorActions {
  // System prompt
  updateSystemPromptJson: (json: string) => void;
  toggleSystemPromptExpanded: () => void;

  // Editor preferences
  updateSystemPromptEditorPrefs: (patch: Partial<SystemPromptEditorPrefs>) => void;

  // Screen navigation
  setActiveScreen: (id: SlotScreenId) => void;
  updateScreenPrompt: (id: SlotScreenId, prompt: string) => void;
  addScreenReferenceImage: (id: SlotScreenId, url: string) => void;
  removeScreenReferenceImage: (id: SlotScreenId, index: number) => void;

  // Generation lifecycle
  startScreenGeneration: (screenId: SlotScreenId) => void;
  addScreenResult: (screenId: SlotScreenId, result: ScreenResult) => void;
  setScreenGenerationError: (screenId: SlotScreenId, error: string) => void;
  finishScreenGeneration: (screenId: SlotScreenId) => void;
  selectScreenResult: (screenId: SlotScreenId, resultId: string) => void;
  deleteScreenResult: (screenId: SlotScreenId, resultId: string) => void;

  // Replace a result in-place (used by inpainting + reroll)
  replaceScreenResult: (screenId: SlotScreenId, resultId: string, newResult: ScreenResult) => void;

  // Generation settings
  updateGenerationSettings: (patch: Partial<ScreenGenerationSettings>) => void;
  updateScreenGenerationSettings: (
    screenId: SlotScreenId,
    patch: { generationModel?: string; generationImageCount?: 1 | 2 | 4 | 8 },
  ) => void;

  // Section tab
  setActiveSection: (section: 'screens-video' | 'screen-builder' | 'canvas') => void;

  // Full view
  openFullView: (screenId: SlotScreenId, index: number) => void;
  closeFullView: () => void;
  navigateFullView: (direction: 'prev' | 'next') => void;

  // Idea Canvas (multi-session chat)
  createIdeaSession: (modelId?: IdeaChatModelId) => string;
  deleteIdeaSession: (sessionId: string) => void;
  setActiveIdeaSession: (sessionId: string | null) => void;
  renameIdeaSession: (sessionId: string, title: string) => void;
  setIdeaCanvasModel: (modelId: IdeaChatModelId) => void;
  addIdeaMessage: (sessionId: string, msg: IdeaChatMessage) => void;
  setIdeaGenerating: (generating: boolean) => void;
  clearIdeaSession: (sessionId: string) => void;
  appendIdeaMessageText: (sessionId: string, msgId: string, textDelta: string) => void;
  appendIdeaMessageThought: (sessionId: string, msgId: string, thoughtDelta: string) => void;
  updateIdeaMessage: (sessionId: string, msgId: string, patch: Partial<IdeaChatMessage>) => void;
  updateIdeaMessageSavedPaths: (sessionId: string, msgId: string, paths: string[]) => void;
  restoreIdeaMessageImages: (sessionId: string, msgId: string, blobUrls: string[]) => void;

  // Video
  setVideoConfig: (patch: Partial<VideoCompilationConfig>) => void;
  setVideoState: (patch: Partial<VideoCompilationState>) => void;
  resetVideo: () => void;

  // Video slots
  setVideoSlotImage: (screenId: SlotScreenId, imageUrl: string | null, source: 'generated' | 'upload') => void;
  clearVideoSlotImage: (screenId: SlotScreenId) => void;
  setVideoSlotAnimationPrompt: (screenId: SlotScreenId, prompt: string) => void;
  setVideoSlotAnimationSystemPrompt: (screenId: SlotScreenId, prompt: string) => void;
  resetVideoSlotAnimationSystemPrompt: (screenId: SlotScreenId) => void;
  syncVideoSlotsFromScreens: () => void;
  setVideoTotalDuration: (seconds: number) => void;
  setVideoResolutionPreset: (preset: VideoResolutionPreset) => void;

  // Video history
  addVideoHistoryEntry: (entry: VideoGenerationHistoryEntry) => void;
  removeVideoHistoryEntry: (id: string) => void;
  updateVideoHistoryEntry: (id: string, patch: Partial<VideoGenerationHistoryEntry>) => void;

  // Image persistence
  updateResultSavedPath: (screenId: SlotScreenId, resultId: string, savedPath: string) => void;
  restoreResultImageUrl: (screenId: SlotScreenId, resultId: string, imageUrl: string) => void;

  // Video persistence
  updateVideoSavedPath: (entryId: string, savedPath: string) => void;
  restoreVideoUrl: (entryId: string, videoUrl: string) => void;

  // Prompt editor drawer
  openPromptDrawer: (screenId: SlotScreenId, field: 'description' | 'animation' | 'animation-system') => void;
  closePromptDrawer: () => void;

  // Toasts
  addToast: (toast: ScreenSavedToast) => void;
  removeToast: (id: string) => void;

  // Screen Builder - Component selection & navigation
  selectComponent: (componentId: string | null) => void;
  openGeneratorView: (componentId: string) => void;
  closeGeneratorView: () => void;
  openGeneratorFullView: (index: number) => void;
  closeGeneratorFullView: () => void;
  navigateGeneratorFullView: (direction: 'prev' | 'next') => void;

  // Screen Builder - Component layout
  updateComponentLayout: (screenId: SlotScreenId, componentId: string, patch: { x?: number; y?: number; width?: number; height?: number }) => void;
  updateComponentZIndex: (screenId: SlotScreenId, componentId: string, zIndex: number) => void;
  toggleComponentLock: (screenId: SlotScreenId, componentId: string) => void;
  toggleComponentVisible: (screenId: SlotScreenId, componentId: string) => void;
  resetComponentLayout: (screenId: SlotScreenId, componentId: string) => void;

  // Screen Builder - Component generation
  updateComponentPrompt: (screenId: SlotScreenId, componentId: string, prompt: string) => void;
  setComponentGenerationMode: (screenId: SlotScreenId, componentId: string, mode: 'image' | 'video') => void;
  updateComponentGenerationSettings: (screenId: SlotScreenId, componentId: string, patch: { generationModel?: string; generationImageCount?: 1 | 2 | 4 | 8 }) => void;
  addComponentReferenceImage: (screenId: SlotScreenId, componentId: string, url: string) => void;
  removeComponentReferenceImage: (screenId: SlotScreenId, componentId: string, index: number) => void;
  startComponentGeneration: (screenId: SlotScreenId, componentId: string) => void;
  addComponentResult: (screenId: SlotScreenId, componentId: string, result: ComponentResult) => void;
  finishComponentGeneration: (screenId: SlotScreenId, componentId: string) => void;
  selectComponentResult: (screenId: SlotScreenId, componentId: string, resultId: string) => void;
  selectComponentVideoResult: (screenId: SlotScreenId, componentId: string, resultId: string) => void;
  deleteComponentResult: (screenId: SlotScreenId, componentId: string, resultId: string) => void;
  updateComponentResultSavedPath: (screenId: SlotScreenId, componentId: string, resultId: string, savedPath: string) => void;
  restoreComponentResultUrl: (screenId: SlotScreenId, componentId: string, resultId: string, imageUrl: string, videoUrl?: string) => void;

  // Screen Builder - Canvas
  setCanvasZoom: (zoom: number) => void;
}

export const useSlotScreenGeneratorStore = create<SlotScreenGeneratorState & SlotScreenGeneratorActions>()(
  persist(
    immer((set, get) => ({
      // === State ===
      systemPromptJson: DEFAULT_SYSTEM_PROMPT_JSON,
      isSystemPromptExpanded: false,
      systemPromptEditorPrefs: { ...DEFAULT_EDITOR_PREFS },
      screens: buildInitialScreens(),
      activeScreenId: 'splash' as SlotScreenId,
      generationSettings: { ...DEFAULT_GENERATION_SETTINGS },
      ideaCanvas: {
        sessions: [],
        activeSessionId: null,
        isGenerating: false,
        defaultModelId: 'gemini-3-flash-preview',
      } as IdeaCanvasState,
      activeSection: 'screens-video' as const,
      videoConfig: { ...DEFAULT_VIDEO_CONFIG },
      videoState: {
        status: 'idle',
        progress: 0,
        outputUrl: null,
        error: null,
        currentGenerationId: null,
        currentScreenStatus: null,
      } as VideoCompilationState,
      videoSlotImages: buildInitialVideoSlotImages(),
      videoHistory: [] as VideoGenerationHistoryEntry[],
      fullViewScreenId: null as SlotScreenId | null,
      fullViewIndex: null as number | null,
      promptDrawerScreenId: null as SlotScreenId | null,
      promptDrawerField: null as 'description' | 'animation' | 'animation-system' | null,
      toasts: [] as ScreenSavedToast[],

      // === Screen Builder ===
      screenComponents: buildInitialScreenComponents(),
      selectedComponentId: null as string | null,
      canvasZoom: 1,
      generatorViewComponentId: null as string | null,
      generatorFullViewIndex: null as number | null,

      // === System Prompt ===
      updateSystemPromptJson: (json: string) => {
        set((state) => {
          state.systemPromptJson = json;
        });
      },

      toggleSystemPromptExpanded: () => {
        set((state) => {
          state.isSystemPromptExpanded = !state.isSystemPromptExpanded;
        });
      },

      updateSystemPromptEditorPrefs: (patch: Partial<SystemPromptEditorPrefs>) => {
        set((state) => {
          Object.assign(state.systemPromptEditorPrefs, patch);
          if (patch.fontSize !== undefined) {
            state.systemPromptEditorPrefs.fontSize = Math.max(10, Math.min(24, patch.fontSize));
          }
        });
      },

      // === Screen Navigation ===
      setActiveScreen: (id: SlotScreenId) => {
        set((state) => {
          state.activeScreenId = id;
        });
      },

      updateScreenPrompt: (id: SlotScreenId, prompt: string) => {
        set((state) => {
          if (state.screens[id]) {
            state.screens[id].userPrompt = prompt;
          }
        });
      },

      addScreenReferenceImage: (id: SlotScreenId, url: string) => {
        set((state) => {
          if (state.screens[id]) {
            state.screens[id].referenceImages.push(url);
          }
        });
      },

      removeScreenReferenceImage: (id: SlotScreenId, index: number) => {
        set((state) => {
          if (state.screens[id]) {
            state.screens[id].referenceImages.splice(index, 1);
          }
        });
      },

      // === Generation Lifecycle ===
      startScreenGeneration: (screenId: SlotScreenId) => {
        set((state) => {
          if (state.screens[screenId]) {
            state.screens[screenId].status = 'generating';
            // Do NOT clear generatedResults — new generations append variants
          }
        });
      },

      addScreenResult: (screenId: SlotScreenId, result: ScreenResult) => {
        set((state) => {
          if (state.screens[screenId]) {
            state.screens[screenId].generatedResults.push(result);
            // Auto-select if no valid selection exists (handles null or stale selectedResultId)
            if (result.status === 'ready') {
              const currentId = state.screens[screenId].selectedResultId;
              const selectionValid = currentId
                ? state.screens[screenId].generatedResults.some(r => r.id === currentId && r.status === 'ready')
                : false;
              if (!selectionValid) {
                state.screens[screenId].selectedResultId = result.id;
                state.screens[screenId].status = 'selected';
                // Immediately sync videoSlotImages so VideoControlBar filledSlots updates
                const slot = state.videoSlotImages[screenId];
                if (slot && slot.source !== 'upload' && result.imageUrl) {
                  slot.imageUrl = result.imageUrl;
                  slot.source = 'generated';
                }
              }
            }
          }
        });
      },

      setScreenGenerationError: (screenId: SlotScreenId, error: string) => {
        set((state) => {
          if (state.screens[screenId]) {
            const screen = state.screens[screenId];
            // If we have some results, keep them
            if (screen.generatedResults.length > 0) {
              screen.status = 'has-results';
            } else {
              screen.status = 'empty';
            }
          }
        });
      },

      finishScreenGeneration: (screenId: SlotScreenId) => {
        set((state) => {
          if (state.screens[screenId]) {
            const screen = state.screens[screenId];
            // Auto-select the first ready result if nothing is selected yet
            if (!screen.selectedResultId) {
              const first = screen.generatedResults.find(r => r.status === 'ready');
              if (first) {
                screen.selectedResultId = first.id;
                screen.status = 'selected';
                // Immediately sync videoSlotImages
                const slot = state.videoSlotImages[screenId];
                if (slot && slot.source !== 'upload' && first.imageUrl) {
                  slot.imageUrl = first.imageUrl;
                  slot.source = 'generated';
                }
                return;
              }
            }
            screen.status = screen.selectedResultId ? 'selected' : 'has-results';
          }
        });
      },

      selectScreenResult: (screenId: SlotScreenId, resultId: string) => {
        set((state) => {
          if (state.screens[screenId]) {
            state.screens[screenId].selectedResultId = resultId;
            state.screens[screenId].status = 'selected';
          }
        });
      },

      deleteScreenResult: (screenId: SlotScreenId, resultId: string) => {
        // Capture savedPath before mutating (for async file deletion)
        const savedPath = get().screens[screenId]?.generatedResults.find(r => r.id === resultId)?.savedPath;

        set((state) => {
          const screen = state.screens[screenId];
          if (!screen) return;

          // Find and remove the result
          const idx = screen.generatedResults.findIndex((r) => r.id === resultId);
          if (idx === -1) return;

          const result = screen.generatedResults[idx];

          // Revoke blob URL if present
          if (result.imageUrl && result.imageUrl.startsWith('blob:')) {
            try { URL.revokeObjectURL(result.imageUrl); } catch { /* ignore */ }
          }

          screen.generatedResults.splice(idx, 1);

          // Update selection and status
          if (screen.selectedResultId === resultId) {
            screen.selectedResultId = null;
            screen.status = screen.generatedResults.length > 0 ? 'has-results' : 'empty';
          }
        });

        // Delete saved file from project folder (fire-and-forget)
        if (savedPath) {
          import('../services/slot-screen-generator/SlotScreenImageService').then(
            ({ deleteScreenResultImage }) => deleteScreenResultImage(savedPath).catch(() => {}),
          );
        }
      },

      // === Replace Result (inpainting / reroll) ===
      replaceScreenResult: (screenId: SlotScreenId, resultId: string, newResult: ScreenResult) => {
        set((state) => {
          const screen = state.screens[screenId];
          if (!screen) return;

          const idx = screen.generatedResults.findIndex((r) => r.id === resultId);
          if (idx === -1) return;

          const old = screen.generatedResults[idx];
          if (old.imageUrl && old.imageUrl.startsWith('blob:')) {
            try { URL.revokeObjectURL(old.imageUrl); } catch { /* ignore */ }
          }

          screen.generatedResults[idx] = newResult;

          // Preserve selection if the replaced result was selected
          if (screen.selectedResultId === resultId) {
            screen.selectedResultId = newResult.id;
            // Immediately sync video slot so storyboard card updates
            const slot = state.videoSlotImages[screenId];
            if (slot && slot.source !== 'upload' && newResult.imageUrl) {
              slot.imageUrl = newResult.imageUrl;
              slot.source = 'generated';
            }
          }
        });
      },

      // === Generation Settings ===
      updateGenerationSettings: (patch: Partial<ScreenGenerationSettings>) => {
        set((state) => {
          Object.assign(state.generationSettings, patch);
        });
      },

      updateScreenGenerationSettings: (
        screenId: SlotScreenId,
        patch: { generationModel?: string; generationImageCount?: 1 | 2 | 4 | 8 },
      ) => {
        set((state) => {
          const screen = state.screens[screenId];
          if (!screen) return;
          if ('generationModel' in patch) screen.generationModel = patch.generationModel;
          if ('generationImageCount' in patch) screen.generationImageCount = patch.generationImageCount;
        });
      },

      // === Section Tab ===
      setActiveSection: (section: 'screens-video' | 'screen-builder' | 'canvas') => {
        set((state) => {
          state.activeSection = section;
        });
      },

      // === Full View ===
      openFullView: (screenId: SlotScreenId, index: number) => {
        set((state) => {
          state.fullViewScreenId = screenId;
          state.fullViewIndex = index;
        });
      },

      closeFullView: () => {
        set((state) => {
          state.fullViewScreenId = null;
          state.fullViewIndex = null;
        });
      },

      navigateFullView: (direction: 'prev' | 'next') => {
        set((state) => {
          if (!state.fullViewScreenId || state.fullViewIndex === null) return;

          const screen = state.screens[state.fullViewScreenId];
          if (!screen || screen.generatedResults.length === 0) return;

          const count = screen.generatedResults.length;
          if (direction === 'next') {
            state.fullViewIndex = (state.fullViewIndex + 1) % count;
          } else {
            state.fullViewIndex = (state.fullViewIndex - 1 + count) % count;
          }
        });
      },

      // === Idea Canvas (Multi-Session Chat) ===
      createIdeaSession: (modelId?: IdeaChatModelId) => {
        const id = generateId();
        set((state) => {
          const session: IdeaChatSession = {
            id,
            title: 'New Chat',
            modelId: modelId ?? state.ideaCanvas.defaultModelId,
            messages: [],
            createdAt: Date.now(),
            updatedAt: Date.now(),
          };
          state.ideaCanvas.sessions.unshift(session);
          state.ideaCanvas.activeSessionId = id;
        });
        return id;
      },

      deleteIdeaSession: (sessionId: string) => {
        set((state) => {
          const session = state.ideaCanvas.sessions.find(s => s.id === sessionId);
          if (session) {
            // Revoke blob URLs
            for (const msg of session.messages) {
              for (const url of msg.images) {
                if (url?.startsWith('blob:')) try { URL.revokeObjectURL(url); } catch { /* ignore */ }
              }
            }
          }
          state.ideaCanvas.sessions = state.ideaCanvas.sessions.filter(s => s.id !== sessionId);
          if (state.ideaCanvas.activeSessionId === sessionId) {
            state.ideaCanvas.activeSessionId = state.ideaCanvas.sessions[0]?.id ?? null;
          }
        });
        // Delete saved files (fire-and-forget)
        import('../services/slot-screen-generator/SlotScreenImageService').then(
          ({ deleteIdeaSessionFiles }) => deleteIdeaSessionFiles(sessionId).catch(() => {}),
        );
      },

      setActiveIdeaSession: (sessionId: string | null) => {
        set((state) => { state.ideaCanvas.activeSessionId = sessionId; });
      },

      renameIdeaSession: (sessionId: string, title: string) => {
        set((state) => {
          const session = state.ideaCanvas.sessions.find(s => s.id === sessionId);
          if (session) session.title = title;
        });
      },

      setIdeaCanvasModel: (modelId: IdeaChatModelId) => {
        set((state) => {
          state.ideaCanvas.defaultModelId = modelId;
          const session = state.ideaCanvas.sessions.find(s => s.id === state.ideaCanvas.activeSessionId);
          if (session) session.modelId = modelId;
        });
      },

      addIdeaMessage: (sessionId: string, msg: IdeaChatMessage) => {
        set((state) => {
          const session = state.ideaCanvas.sessions.find(s => s.id === sessionId);
          if (!session) return;
          session.messages.push(msg);
          session.updatedAt = Date.now();
          // Auto-set title from first user message
          if (session.title === 'New Chat' && msg.role === 'user' && msg.text) {
            session.title = msg.text.substring(0, 50).trim() || 'New Chat';
          }
        });
      },

      setIdeaGenerating: (generating: boolean) => {
        set((state) => { state.ideaCanvas.isGenerating = generating; });
      },

      clearIdeaSession: (sessionId: string) => {
        set((state) => {
          const session = state.ideaCanvas.sessions.find(s => s.id === sessionId);
          if (session) {
            for (const msg of session.messages) {
              for (const url of msg.images) {
                if (url?.startsWith('blob:')) try { URL.revokeObjectURL(url); } catch { /* ignore */ }
              }
            }
            session.messages = [];
          }
        });
      },

      updateIdeaMessageSavedPaths: (sessionId: string, msgId: string, paths: string[]) => {
        set((state) => {
          const session = state.ideaCanvas.sessions.find(s => s.id === sessionId);
          if (!session) return;
          const msg = session.messages.find(m => m.id === msgId);
          if (msg) msg.savedImagePaths = paths;
        });
      },

      appendIdeaMessageText: (sessionId: string, msgId: string, textDelta: string) => {
        set((state) => {
          const session = state.ideaCanvas.sessions.find(s => s.id === sessionId);
          if (!session) return;
          const msg = session.messages.find(m => m.id === msgId);
          if (msg) msg.text += textDelta;
        });
      },

      appendIdeaMessageThought: (sessionId: string, msgId: string, thoughtDelta: string) => {
        set((state) => {
          const session = state.ideaCanvas.sessions.find(s => s.id === sessionId);
          if (!session) return;
          const msg = session.messages.find(m => m.id === msgId);
          if (msg) msg.thoughtText = (msg.thoughtText ?? '') + thoughtDelta;
        });
      },

      updateIdeaMessage: (sessionId: string, msgId: string, patch: Partial<IdeaChatMessage>) => {
        set((state) => {
          const session = state.ideaCanvas.sessions.find(s => s.id === sessionId);
          if (!session) return;
          const msg = session.messages.find(m => m.id === msgId);
          if (msg) Object.assign(msg, patch);
        });
      },

      restoreIdeaMessageImages: (sessionId: string, msgId: string, blobUrls: string[]) => {
        set((state) => {
          const session = state.ideaCanvas.sessions.find(s => s.id === sessionId);
          if (!session) return;
          const msg = session.messages.find(m => m.id === msgId);
          if (msg) msg.images = blobUrls;
        });
      },

      // === Video ===
      setVideoConfig: (patch: Partial<VideoCompilationConfig>) => {
        set((state) => {
          Object.assign(state.videoConfig, patch);
        });
      },

      setVideoState: (patch: Partial<VideoCompilationState>) => {
        set((state) => {
          Object.assign(state.videoState, patch);
        });
      },

      resetVideo: () => {
        set((state) => {
          state.videoState = {
            status: 'idle',
            progress: 0,
            outputUrl: null,
            error: null,
          };
        });
      },

      // === Video Slots ===
      setVideoSlotImage: (screenId: SlotScreenId, imageUrl: string | null, source: 'generated' | 'upload') => {
        set((state) => {
          state.videoSlotImages[screenId].imageUrl = imageUrl;
          state.videoSlotImages[screenId].source = source;
        });
      },

      clearVideoSlotImage: (screenId: SlotScreenId) => {
        set((state) => {
          state.videoSlotImages[screenId].imageUrl = null;
          state.videoSlotImages[screenId].source = 'generated';
        });
      },

      setVideoSlotAnimationPrompt: (screenId: SlotScreenId, prompt: string) => {
        set((state) => {
          state.videoSlotImages[screenId].animationPrompt = prompt;
        });
      },

      setVideoSlotAnimationSystemPrompt: (screenId: SlotScreenId, prompt: string) => {
        set((state) => {
          state.videoSlotImages[screenId].animationSystemPrompt = prompt;
        });
      },

      resetVideoSlotAnimationSystemPrompt: (screenId: SlotScreenId) => {
        set((state) => {
          state.videoSlotImages[screenId].animationSystemPrompt = DEFAULT_ANIMATION_SYSTEM_PROMPTS[screenId];
        });
      },

      syncVideoSlotsFromScreens: () => {
        set((state) => {
          for (const id of SLOT_SCREEN_ORDER) {
            const slot = state.videoSlotImages[id];
            // Don't overwrite user uploads
            if (slot.source === 'upload') continue;
            const screen = state.screens[id];
            if (screen.selectedResultId) {
              const result = screen.generatedResults.find((r) => r.id === screen.selectedResultId);
              if (result?.imageUrl) {
                slot.imageUrl = result.imageUrl;
                slot.source = 'generated';
              } else {
                // Selected result has no image (or was deleted) — clear slot
                slot.imageUrl = null;
              }
            } else {
              // No selected result — clear the slot
              slot.imageUrl = null;
            }
          }
        });
      },

      setVideoTotalDuration: (seconds: number) => {
        set((state) => {
          state.videoConfig.totalDuration = Math.max(5, Math.min(120, seconds));
        });
      },

      setVideoResolutionPreset: (preset: VideoResolutionPreset) => {
        set((state) => {
          state.videoConfig.resolutionPreset = preset;
          const dims = VIDEO_RESOLUTION_PRESETS[preset];
          if (dims) {
            state.videoConfig.width = dims.width;
            state.videoConfig.height = dims.height;
          }
        });
      },

      // === Video History ===
      addVideoHistoryEntry: (entry: VideoGenerationHistoryEntry) => {
        set((state) => {
          state.videoHistory.unshift(entry);
          if (state.videoHistory.length > 50) {
            state.videoHistory = state.videoHistory.slice(0, 50);
          }
        });
      },

      removeVideoHistoryEntry: (id: string) => {
        const savedPath = get().videoHistory.find(e => e.id === id)?.savedPath;
        set((state) => {
          const entry = state.videoHistory.find((e) => e.id === id);
          if (entry?.videoUrl?.startsWith('blob:')) {
            try { URL.revokeObjectURL(entry.videoUrl); } catch { /* ignore */ }
          }
          state.videoHistory = state.videoHistory.filter((e) => e.id !== id);
        });
        // Delete saved file from project folder (fire-and-forget)
        if (savedPath) {
          import('../services/slot-screen-generator/SlotScreenImageService').then(
            ({ deleteVideoFromProject }) => deleteVideoFromProject(savedPath).catch(() => {}),
          );
        }
      },

      updateVideoHistoryEntry: (id: string, patch: Partial<VideoGenerationHistoryEntry>) => {
        set((state) => {
          const entry = state.videoHistory.find((e) => e.id === id);
          if (entry) Object.assign(entry, patch);
        });
      },

      // === Image Persistence ===
      updateResultSavedPath: (screenId: SlotScreenId, resultId: string, savedPath: string) => {
        set((state) => {
          const screen = state.screens[screenId];
          if (!screen) return;
          const result = screen.generatedResults.find(r => r.id === resultId);
          if (result) result.savedPath = savedPath;
        });
      },

      restoreResultImageUrl: (screenId: SlotScreenId, resultId: string, imageUrl: string) => {
        set((state) => {
          const screen = state.screens[screenId];
          if (!screen) return;
          const result = screen.generatedResults.find(r => r.id === resultId);
          if (result) result.imageUrl = imageUrl;
        });
      },

      // === Video Persistence ===
      updateVideoSavedPath: (entryId: string, savedPath: string) => {
        set((state) => {
          const entry = state.videoHistory.find(e => e.id === entryId);
          if (entry) entry.savedPath = savedPath;
        });
      },

      restoreVideoUrl: (entryId: string, videoUrl: string) => {
        set((state) => {
          const entry = state.videoHistory.find(e => e.id === entryId);
          if (entry) {
            entry.videoUrl = videoUrl;
            entry.status = 'ready';
            entry.error = undefined;
          }
        });
      },

      // === Prompt Editor Drawer ===
      openPromptDrawer: (screenId: SlotScreenId, field: 'description' | 'animation' | 'animation-system') => {
        set((state) => {
          state.promptDrawerScreenId = screenId;
          state.promptDrawerField = field;
        });
      },

      closePromptDrawer: () => {
        set((state) => {
          state.promptDrawerScreenId = null;
          state.promptDrawerField = null;
        });
      },

      // === Toasts ===
      addToast: (toast: ScreenSavedToast) => {
        set((state) => {
          state.toasts.push(toast);
        });
      },

      removeToast: (id: string) => {
        set((state) => {
          state.toasts = state.toasts.filter((t) => t.id !== id);
        });
      },

      // === Screen Builder - Selection & Navigation ===
      selectComponent: (componentId: string | null) => {
        set((state) => { state.selectedComponentId = componentId; });
      },

      openGeneratorView: (componentId: string) => {
        set((state) => {
          state.generatorViewComponentId = componentId;
          state.selectedComponentId = componentId;
        });
      },

      closeGeneratorView: () => {
        set((state) => {
          state.generatorViewComponentId = null;
          state.generatorFullViewIndex = null;
        });
      },

      openGeneratorFullView: (index: number) => {
        set((state) => { state.generatorFullViewIndex = index; });
      },

      closeGeneratorFullView: () => {
        set((state) => { state.generatorFullViewIndex = null; });
      },

      navigateGeneratorFullView: (direction: 'prev' | 'next') => {
        set((state) => {
          if (state.generatorFullViewIndex === null || !state.generatorViewComponentId) return;
          const comp = state.screenComponents[state.activeScreenId]?.find(c => c.id === state.generatorViewComponentId);
          if (!comp) return;
          const readyResults = comp.generatedResults.filter(r => r.status === 'ready');
          const len = readyResults.length;
          if (len === 0) return;
          if (direction === 'next') {
            state.generatorFullViewIndex = (state.generatorFullViewIndex + 1) % len;
          } else {
            state.generatorFullViewIndex = (state.generatorFullViewIndex - 1 + len) % len;
          }
        });
      },

      // === Screen Builder - Layout ===
      updateComponentLayout: (screenId, componentId, patch) => {
        set((state) => {
          const comp = state.screenComponents[screenId]?.find(c => c.id === componentId);
          if (!comp || comp.locked) return;
          if (patch.x !== undefined) comp.x = patch.x;
          if (patch.y !== undefined) comp.y = patch.y;
          if (patch.width !== undefined) comp.width = Math.max(10, patch.width);
          if (patch.height !== undefined) comp.height = Math.max(10, patch.height);
        });
      },

      updateComponentZIndex: (screenId, componentId, zIndex) => {
        set((state) => {
          const comp = state.screenComponents[screenId]?.find(c => c.id === componentId);
          if (comp) comp.zIndex = zIndex;
        });
      },

      toggleComponentLock: (screenId, componentId) => {
        set((state) => {
          const comp = state.screenComponents[screenId]?.find(c => c.id === componentId);
          if (comp) comp.locked = !comp.locked;
        });
      },

      toggleComponentVisible: (screenId, componentId) => {
        set((state) => {
          const comp = state.screenComponents[screenId]?.find(c => c.id === componentId);
          if (comp) comp.visible = !comp.visible;
        });
      },

      resetComponentLayout: (screenId, componentId) => {
        set((state) => {
          const comp = state.screenComponents[screenId]?.find(c => c.id === componentId);
          if (!comp) return;
          const defaults = DEFAULT_SCREEN_COMPONENTS[screenId]?.find(d => d.id === componentId);
          if (!defaults) return;
          comp.x = defaults.x;
          comp.y = defaults.y;
          comp.width = defaults.width;
          comp.height = defaults.height;
          comp.zIndex = defaults.zIndex;
          comp.locked = false;
        });
      },

      // === Screen Builder - Generation ===
      updateComponentPrompt: (screenId, componentId, prompt) => {
        set((state) => {
          const comp = state.screenComponents[screenId]?.find(c => c.id === componentId);
          if (comp) comp.userPrompt = prompt;
        });
      },

      setComponentGenerationMode: (screenId, componentId, mode) => {
        set((state) => {
          const comp = state.screenComponents[screenId]?.find(c => c.id === componentId);
          if (comp) comp.generationMode = mode;
        });
      },

      updateComponentGenerationSettings: (screenId, componentId, patch) => {
        set((state) => {
          const comp = state.screenComponents[screenId]?.find(c => c.id === componentId);
          if (!comp) return;
          if (patch.generationModel !== undefined) comp.generationModel = patch.generationModel;
          if (patch.generationImageCount !== undefined) comp.generationImageCount = patch.generationImageCount;
        });
      },

      addComponentReferenceImage: (screenId, componentId, url) => {
        set((state) => {
          const comp = state.screenComponents[screenId]?.find(c => c.id === componentId);
          if (comp) comp.referenceImages.push(url);
        });
      },

      removeComponentReferenceImage: (screenId, componentId, index) => {
        set((state) => {
          const comp = state.screenComponents[screenId]?.find(c => c.id === componentId);
          if (comp) comp.referenceImages.splice(index, 1);
        });
      },

      startComponentGeneration: (screenId, componentId) => {
        set((state) => {
          const comp = state.screenComponents[screenId]?.find(c => c.id === componentId);
          if (comp) comp.status = 'generating';
        });
      },

      addComponentResult: (screenId, componentId, result) => {
        set((state) => {
          const comp = state.screenComponents[screenId]?.find(c => c.id === componentId);
          if (!comp) return;
          comp.generatedResults.push(result);
          // Auto-select first ready result
          if (result.status === 'ready' && result.type === 'image' && !comp.selectedResultId) {
            comp.selectedResultId = result.id;
            comp.status = 'selected';
          }
          if (result.status === 'ready' && result.type === 'video' && !comp.selectedVideoResultId) {
            comp.selectedVideoResultId = result.id;
          }
        });
      },

      finishComponentGeneration: (screenId, componentId) => {
        set((state) => {
          const comp = state.screenComponents[screenId]?.find(c => c.id === componentId);
          if (!comp) return;
          if (comp.selectedResultId) {
            comp.status = 'selected';
          } else if (comp.generatedResults.some(r => r.status === 'ready')) {
            comp.status = 'has-results';
            const first = comp.generatedResults.find(r => r.status === 'ready' && r.type === 'image');
            if (first) { comp.selectedResultId = first.id; comp.status = 'selected'; }
          } else {
            comp.status = 'placeholder';
          }
        });
      },

      selectComponentResult: (screenId, componentId, resultId) => {
        set((state) => {
          const comp = state.screenComponents[screenId]?.find(c => c.id === componentId);
          if (!comp) return;
          comp.selectedResultId = resultId;
          comp.status = 'selected';
        });
      },

      selectComponentVideoResult: (screenId, componentId, resultId) => {
        set((state) => {
          const comp = state.screenComponents[screenId]?.find(c => c.id === componentId);
          if (comp) comp.selectedVideoResultId = resultId;
        });
      },

      deleteComponentResult: (screenId, componentId, resultId) => {
        set((state) => {
          const comp = state.screenComponents[screenId]?.find(c => c.id === componentId);
          if (!comp) return;
          comp.generatedResults = comp.generatedResults.filter(r => r.id !== resultId);
          if (comp.selectedResultId === resultId) comp.selectedResultId = null;
          if (comp.selectedVideoResultId === resultId) comp.selectedVideoResultId = null;
          if (comp.generatedResults.length === 0) {
            comp.status = 'placeholder';
          } else if (!comp.selectedResultId && comp.generatedResults.some(r => r.status === 'ready')) {
            comp.status = 'has-results';
          }
        });
      },

      updateComponentResultSavedPath: (screenId, componentId, resultId, savedPath) => {
        set((state) => {
          const comp = state.screenComponents[screenId]?.find(c => c.id === componentId);
          if (!comp) return;
          const r = comp.generatedResults.find(r => r.id === resultId);
          if (r) r.savedPath = savedPath;
        });
      },

      restoreComponentResultUrl: (screenId, componentId, resultId, imageUrl, videoUrl) => {
        set((state) => {
          const comp = state.screenComponents[screenId]?.find(c => c.id === componentId);
          if (!comp) return;
          const r = comp.generatedResults.find(r => r.id === resultId);
          if (r) {
            r.imageUrl = imageUrl;
            if (videoUrl !== undefined) r.videoUrl = videoUrl;
          }
        });
      },

      // === Screen Builder - Canvas ===
      setCanvasZoom: (zoom: number) => {
        set((state) => { state.canvasZoom = Math.max(0.1, Math.min(3, zoom)); });
      },
    })),
    {
      name: 'slot-screen-generator-store',
      partialize: (state) => ({
        systemPromptJson: state.systemPromptJson,
        isSystemPromptExpanded: state.isSystemPromptExpanded,
        systemPromptEditorPrefs: state.systemPromptEditorPrefs,
        screens: Object.fromEntries(
          Object.entries(state.screens).map(([id, screen]) => [
            id,
            {
              ...screen,
              // Keep results saved to disk (strip blob URLs — they won't survive reload)
              generatedResults: screen.generatedResults
                .filter(r => r.status === 'ready' && r.savedPath)
                .map(r => ({ ...r, imageUrl: '' })),
              status: screen.selectedResultId ? 'selected' :
                screen.generatedResults.some(r => r.savedPath) ? 'has-results' : 'empty',
            },
          ])
        ),
        activeScreenId: state.activeScreenId,
        generationSettings: state.generationSettings,
        activeSection: state.activeSection,
        videoConfig: state.videoConfig,
        // Persist animation prompts but not blob URLs (they won't survive reload)
        videoSlotImages: Object.fromEntries(
          Object.entries(state.videoSlotImages).map(([id, slot]) => [
            id,
            { ...slot, imageUrl: null, source: 'generated' as const },
          ])
        ),
        // Keep video entries saved to disk (strip blob URLs)
        videoHistory: state.videoHistory
          .filter(e => e.status === 'ready' && e.savedPath)
          .map((e) => ({
            ...e,
            videoUrl: '',
            thumbnailUrl: null,
          })),
        // Persist idea canvas sessions (strip blob URLs, keep savedImagePaths + thoughtSignature)
        ideaCanvas: {
          sessions: state.ideaCanvas.sessions.map(session => ({
            ...session,
            messages: session.messages.map(msg => ({
              ...msg,
              images: [], // Strip blob URLs
            })),
          })),
          activeSessionId: state.ideaCanvas.activeSessionId,
          isGenerating: false,
          defaultModelId: state.ideaCanvas.defaultModelId,
        },
        // Screen Builder persistence
        screenComponents: Object.fromEntries(
          Object.entries(state.screenComponents).map(([screenId, comps]) => [
            screenId,
            comps.map(comp => ({
              ...comp,
              generatedResults: comp.generatedResults
                .filter(r => r.status === 'ready' && r.savedPath)
                .map(r => ({ ...r, imageUrl: '', videoUrl: undefined })),
              referenceImages: comp.referenceImages,
              status: comp.selectedResultId ? 'selected' as const :
                comp.generatedResults.some(r => r.savedPath) ? 'has-results' as const : 'placeholder' as const,
            })),
          ]),
        ),
        canvasZoom: state.canvasZoom,
      }),
      merge: (persisted, current) => {
        const persistedState = persisted as Partial<SlotScreenGeneratorState> | undefined;
        if (!persistedState) return current;

        // Build screens by merging persisted prompts/selections onto fresh defaults
        const mergedScreens = buildInitialScreens();
        if (persistedState.screens) {
          for (const id of SLOT_SCREEN_ORDER) {
            const saved = (persistedState.screens as Record<string, Partial<ScreenConfig>>)[id];
            if (saved) {
              mergedScreens[id].userPrompt = saved.userPrompt ?? '';
              mergedScreens[id].referenceImages = saved.referenceImages ?? [];
              mergedScreens[id].selectedResultId = saved.selectedResultId ?? null;
              mergedScreens[id].generationModel = saved.generationModel;
              mergedScreens[id].generationImageCount = saved.generationImageCount;
              // Restore saved results (imageUrl is empty, will be hydrated async from disk)
              const savedResults = (saved.generatedResults ?? []).filter(
                (r: Partial<ScreenResult>) => r.savedPath,
              ) as ScreenResult[];
              mergedScreens[id].generatedResults = savedResults;
              mergedScreens[id].status = saved.selectedResultId ? 'selected' :
                savedResults.length > 0 ? 'has-results' : 'empty';
            }
          }
        }

        // Restore video slot animation prompts (images are cleared on reload)
        const initial = buildInitialVideoSlotImages();
        const persistedSlots = persistedState.videoSlotImages as Record<string, Partial<VideoSlotImage>> | undefined;
        const mergedSlots = { ...initial };
        if (persistedSlots) {
          for (const id of SLOT_SCREEN_ORDER) {
            if (persistedSlots[id]?.animationPrompt) {
              mergedSlots[id].animationPrompt = persistedSlots[id].animationPrompt ?? '';
            }
            if (persistedSlots[id]?.animationSystemPrompt !== undefined) {
              mergedSlots[id].animationSystemPrompt = persistedSlots[id].animationSystemPrompt ?? DEFAULT_ANIMATION_SYSTEM_PROMPTS[id];
            }
          }
        }

        // Merge screen builder components
        const mergedComponents = buildInitialScreenComponents();
        const persistedComponents = (persistedState as Record<string, unknown>).screenComponents as Record<string, ScreenComponentDef[]> | undefined;
        if (persistedComponents) {
          for (const screenId of SLOT_SCREEN_ORDER) {
            const savedComps = persistedComponents[screenId];
            if (!savedComps) continue;
            for (const savedComp of savedComps) {
              const target = mergedComponents[screenId as SlotScreenId]?.find(c => c.id === savedComp.id);
              if (!target) continue;
              target.userPrompt = savedComp.userPrompt ?? '';
              target.referenceImages = savedComp.referenceImages ?? [];
              target.selectedResultId = savedComp.selectedResultId ?? null;
              target.selectedVideoResultId = savedComp.selectedVideoResultId ?? null;
              target.generationMode = savedComp.generationMode ?? 'image';
              target.generationModel = savedComp.generationModel;
              target.generationImageCount = savedComp.generationImageCount;
              if (savedComp.x !== undefined) target.x = savedComp.x;
              if (savedComp.y !== undefined) target.y = savedComp.y;
              if (savedComp.width !== undefined) target.width = savedComp.width;
              if (savedComp.height !== undefined) target.height = savedComp.height;
              target.locked = savedComp.locked ?? false;
              target.visible = savedComp.visible ?? true;
              target.generatedResults = (savedComp.generatedResults ?? [])
                .filter((r: Partial<ComponentResult>) => r.savedPath)
                .map(r => ({ ...r, imageUrl: '', videoUrl: undefined })) as ComponentResult[];
              target.status = target.selectedResultId ? 'selected' :
                target.generatedResults.length > 0 ? 'has-results' : 'placeholder';
            }
          }
        }

        return {
          ...current,
          systemPromptJson: persistedState.systemPromptJson ?? DEFAULT_SYSTEM_PROMPT_JSON,
          isSystemPromptExpanded: persistedState.isSystemPromptExpanded ?? false,
          systemPromptEditorPrefs: persistedState.systemPromptEditorPrefs
            ? { ...DEFAULT_EDITOR_PREFS, ...persistedState.systemPromptEditorPrefs }
            : { ...DEFAULT_EDITOR_PREFS },
          screens: mergedScreens,
          activeScreenId: persistedState.activeScreenId ?? 'splash',
          generationSettings: persistedState.generationSettings ?? { ...DEFAULT_GENERATION_SETTINGS },
          activeSection: (['screens-video', 'screen-builder', 'canvas'].includes(persistedState.activeSection as string)
            ? persistedState.activeSection
            : 'screens-video') as 'screens-video' | 'screen-builder' | 'canvas',
          videoConfig: { ...DEFAULT_VIDEO_CONFIG, ...(persistedState.videoConfig ?? {}) },
          videoSlotImages: mergedSlots,
          // Restore saved videos (videoUrl empty, will be hydrated async from disk)
          videoHistory: (persistedState.videoHistory ?? [])
            .filter((e: Partial<VideoGenerationHistoryEntry>) => e.savedPath)
            .map((e: Partial<VideoGenerationHistoryEntry>) => ({
              ...e,
              videoUrl: '',
              thumbnailUrl: null,
              status: 'ready' as const,
              error: undefined,
            })) as VideoGenerationHistoryEntry[],
          // Always reset transient state
          videoState: { status: 'idle', progress: 0, outputUrl: null, error: null, currentGenerationId: null, currentScreenStatus: null },
          fullViewScreenId: null,
          fullViewIndex: null,
          toasts: [],
          // Restore idea canvas sessions (images empty, hydrated async from disk)
          ideaCanvas: {
            sessions: ((persistedState as Record<string, unknown>).ideaCanvas as Partial<IdeaCanvasState> | undefined)?.sessions?.map(
              (s: Partial<IdeaChatSession>) => ({
                ...s,
                messages: (s.messages ?? []).map((m: Partial<IdeaChatMessage>) => ({
                  ...m,
                  images: [], // Will be hydrated async
                })),
              }),
            ) ?? [],
            activeSessionId: ((persistedState as Record<string, unknown>).ideaCanvas as Partial<IdeaCanvasState> | undefined)?.activeSessionId ?? null,
            isGenerating: false,
            defaultModelId: ((persistedState as Record<string, unknown>).ideaCanvas as Partial<IdeaCanvasState> | undefined)?.defaultModelId ?? 'gemini-3-flash-preview',
          } as IdeaCanvasState,
          // Screen Builder
          screenComponents: mergedComponents,
          selectedComponentId: null,
          canvasZoom: ((persistedState as Record<string, unknown>).canvasZoom as number) ?? 1,
          generatorViewComponentId: null,
          generatorFullViewIndex: null,
        };
      },
    },
  ),
);
